import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import ExpandDownIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDown.js';

const CREATE_FORM_DROPDOWN_OPTIONS = [
  { formTypeId: 'speak-up', label: 'Speak up' },
  { formTypeId: 'disclosure', label: 'Conflict of interest' },
] as const;

interface Props {
  canCreateForm: boolean;
  disabled: boolean;
  showDropdown: boolean;
  onCreateForm: () => void;
  onCreateFormWithType: (formTypeId: string) => void;
}

export const FormManagementCreateFormButton = ({
  canCreateForm,
  disabled,
  showDropdown,
  onCreateForm,
  onCreateFormWithType,
}: Props) => {
  const { presets } = useTheme<LensTheme>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!canCreateForm) return null;

  const SplitButtonPresets = presets?.SplitButtonPresets ?? {};
  const bottomRight =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (presets as any)?.MenuPresets?.positionFromAnchorEl?.bottomRight ?? {};

  if (!showDropdown) {
    return (
      <Button
        variant="contained"
        disabled={disabled}
        onClick={disabled ? undefined : onCreateForm}
        startIcon={<AddIcon aria-hidden />}
        data-testid="create-form-button"
        sx={{ whiteSpace: 'nowrap' }}
      >
        Create form
      </Button>
    );
  }

  return (
    <>
      <ButtonGroup
        aria-label="Create form"
        disabled={disabled}
        {...SplitButtonPresets}
      >
        <Button
          variant="contained"
          onClick={disabled ? undefined : onCreateForm}
          startIcon={<AddIcon aria-hidden />}
          data-testid="create-form-button"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Create form
        </Button>
        <Button
          id="create-form-dropdown-toggle"
          variant="contained"
          aria-controls={open ? 'create-form-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          aria-label="Create form options"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorEl(e.currentTarget)
          }
          data-testid="create-form-dropdown-toggle"
        >
          <ExpandDownIcon aria-hidden />
        </Button>
      </ButtonGroup>

      <Menu
        id="create-form-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          list: { 'aria-labelledby': 'create-form-dropdown-toggle' },
        }}
        {...bottomRight}
      >
        {CREATE_FORM_DROPDOWN_OPTIONS.map((option) => (
          <MenuItem
            key={option.formTypeId}
            onClick={() => {
              setAnchorEl(null);
              onCreateFormWithType(option.formTypeId);
            }}
            data-testid={`create-form-option-${option.formTypeId}`}
          >
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
